import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import SecondaryButton from "../Common/SecondaryButton.jsx";
import searchIcon from "../../assets/images/restaurant-portal/search-normal.svg";
import filterIcon from "../../assets/images/restaurant-portal/filter.svg";
import exportIcon from "../../assets/images/restaurant-portal/export.svg";

/* -------------------------------------------------------------------------
 * PageToolbar — title + icon actions + search field, matching ExploreHeader
 * Used at the top of the Redemptions table.
 * ---------------------------------------------------------------------- */

const PageToolbar = ({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onExportClick,
  exportLabel,
  sortBy,
  onSortChange,
  primaryAction,
  filterItems,
  onClearFilters,
  showFilter = true,
  showSearch = true,
  showExport = true,
}) => {
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const toggleFilterDropdown = () => {
    setFilterDropdownOpen((prev) => !prev);
  };

  const handleSortClick = (sortValue) => {
    onSortChange(sortValue);
    setFilterDropdownOpen(false);
  };

  const handleFilterItemClick = (filterValue, onChange) => {
    onChange(filterValue);
    setFilterDropdownOpen(false);
  };

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
    setFilterDropdownOpen(false);
  };

  return (
    <div className="rp-explore-header">
      <h4 className="rp-page__title">{title}</h4>

      <div className="rp-explore-header__icon-actions">
        {showFilter && (
          <Dropdown
            isOpen={filterDropdownOpen}
            toggle={toggleFilterDropdown}
            direction="end"
          >
            <DropdownToggle tag="button" className="rp-explore-icon-btn">
              <img src={filterIcon} alt="Filters" />
            </DropdownToggle>
            <DropdownMenu className="rp-explore-filter-dropdown">
            {filterItems ? (
              <>
                {filterItems.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    <DropdownItem header>{section.header}</DropdownItem>
                    {section.items.map((item, itemIndex) => (
                      <DropdownItem
                        key={itemIndex}
                        onClick={() => handleFilterItemClick(item.value, item.onChange)}
                      >
                        {item.label}
                      </DropdownItem>
                    ))}
                  </div>
                ))}
                {onClearFilters && (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={handleClearFilters}
                      className="clear-filters-btn"
                      style={{ color: '#e23d3d', fontWeight: 600 }}
                    >
                      Clear Filters
                    </DropdownItem>
                  </>
                )}
              </>
            ) : (
              <>
                <DropdownItem header>Sort by</DropdownItem>
                <DropdownItem onClick={() => handleSortClick("alphabetically")}>
                  Alphabetical A to Z
                </DropdownItem>
                <DropdownItem onClick={() => handleSortClick("alphabetically_desc")}>
                  Alphabetical Z to A
                </DropdownItem>
                <DropdownItem onClick={() => handleSortClick("date_desc")}>
                  Newest First
                </DropdownItem>
                <DropdownItem onClick={() => handleSortClick("date_asc")}>
                  Oldest First
                </DropdownItem>
                <DropdownItem onClick={() => handleSortClick("value_desc")}>
                  Highest Value
                </DropdownItem>
                <DropdownItem onClick={() => handleSortClick("value_asc")}>
                  Lowest Value
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </Dropdown>
        )}

        {showExport && onExportClick && (
          <SecondaryButton
            type="button"
            variant="light-orange"
            iconPosition="right"
            icon={exportIcon}
            onClick={onExportClick}
          >
            {exportLabel}
          </SecondaryButton>
        )}
        {primaryAction && (
          <SecondaryButton
            type="button"
            variant="orange"
            iconPosition="right"
            icon={primaryAction.icon || exportIcon}
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </SecondaryButton>
        )}
      </div>

      {showSearch && (
        <div className="rp-explore-search">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
          <img src={searchIcon} alt="Search" className="rp-explore-search__icon" />
        </div>
      )}
    </div>
  );
};

PageToolbar.propTypes = {
  title: PropTypes.string,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  onExportClick: PropTypes.func,
  exportLabel: PropTypes.string,
  sortBy: PropTypes.string,
  onSortChange: PropTypes.func,
  primaryAction: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    icon: PropTypes.string,
  }),
  filterItems: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.any,
          onChange: PropTypes.func,
        })
      ),
    })
  ),
  onClearFilters: PropTypes.func,
};

PageToolbar.defaultProps = {
  title: "",
  searchPlaceholder: "Search by Code",
  onExportClick: () => {},
  exportLabel: "Export CSV",
  sortBy: "date_desc",
  onSortChange: () => {},
  primaryAction: null,
  filterItems: null,
  onClearFilters: null,
};

export default PageToolbar;
